import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as api from "../api/client";
import { construirPayload, construirPdf } from "../domain/contracts";
import { construirComparativo } from "../domain/propuestas";
import { calcularCotizacion, validarCotizacion } from "../domain/pricing";
import {
  CANALES_ENVIO,
  NOMBRE_CANAL,
  errorDestino,
  moneda,
  type CanalEnvio,
} from "../domain/model";
import { useEditor } from "../state/editor";
import type { Cotizacion, ResultadoCalculo, Vendedor } from "../domain/types";
import { ErrorAviso, EstadoOperacion } from "./ui";
import Icono from "./Icono";
import { useConfirmar, usePreguntarSitiosEnCero } from "./Dialogos";

type Resultado = { c: Cotizacion; r: ResultadoCalculo };
// porCrm: el WhatsApp quedó pedido al CRM (plantilla), no confirmado como
// entregado. Va en el recibo para que un reintento parcial no lo olvide.
// enviadoPor: canales que ya salieron (con "ambos" son dos); enviado = todos.
type Recibo = Resultado & {
  enviado: boolean;
  entregada: boolean;
  porCrm?: boolean;
  enviadoPor?: CanalEnvio[];
  // Respuesta del primer intento: el reintento manda el mismo documento.
  incluirSitiosEnCero?: boolean;
};
// Excluye solo los identificadores que devuelve el guardado: un reintento
// parcial no vuelve a guardar/enviar las propuestas ya confirmadas sin cambios.
const huella = (c: Cotizacion) =>
  JSON.stringify({
    ...c,
    registroId: null,
    revision: null,
    numeroOficial: null,
    numeroBaseOficial: null,
  });
export default function AccionesPropuestas({
  vendedor,
  setBusy,
}: {
  vendedor: Vendedor;
  setBusy: (b: boolean) => void;
}) {
  const client = useQueryClient(),
    confirmar = useConfirmar(),
    preguntarSitiosEnCero = usePreguntarSitiosEnCero(),
    lock = useRef(false),
    recibos = useRef(new Map<string, Recibo>()),
    url = useRef("");
  const [error, setError] = useState<unknown>(),
    [status, setStatus] = useState(""),
    [pdf, setPdf] = useState(""),
    [nombrePdf, setNombrePdf] = useState(""),
    [totales, setTotales] = useState<Resultado[]>([]);
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );
  async function comparativo(resultados: Resultado[]) {
    setStatus("Generando PDF comparativo…");
    const response = await api.request(
      "cotizador-generar-pdf-comparativo",
      {
        method: "POST",
        body: JSON.stringify(construirComparativo(resultados, vendedor)),
      },
      120000,
    );
    const blob = await response.blob();
    if (!blob.type.includes("pdf"))
      throw new Error("El servicio no devolvió un PDF comparativo.");
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = URL.createObjectURL(blob);
    setPdf(url.current);
    setNombrePdf(
      `Cotizacion-${resultados[0].c.numeroBaseOficial ?? "BORRADOR"}-comparativo.pdf`,
    );
  }
  // Resumen por canal, según cada recibo (incluye envíos parciales de un
  // intento que falló a mitad): correos y WhatsApp directos van como
  // confirmados; los WhatsApp por plantilla, como pedidos al CRM.
  function textoEnvios(todas: Cotizacion[]) {
    const recibos_ = todas
      .map((c) => recibos.current.get(huella(c)))
      .filter((x): x is Recibo => !!x?.enviadoPor?.length);
    let directos = 0,
      crm = 0;
    for (const x of recibos_)
      for (const canal of x.enviadoPor!)
        if (canal === "whatsapp" && x.porCrm) crm++;
        else directos++;
    return (
      (directos ? ` ${directos} envíos confirmados.` : "") +
      (crm
        ? ` ${crm} te llegan por WhatsApp en unos segundos para que se las reenvíes al cliente.`
        : "")
    );
  }
  async function ejecutar(modo: "guardar" | "entregar" | "comparativo") {
    if (lock.current) return;
    const todas = structuredClone(useEditor.getState().todas());
    const errores = todas.flatMap((c) =>
      validarCotizacion(c).map((e) => `${c.etiquetaPropuesta}: ${e}`),
    );
    if (errores.length) {
      setError(new Error(errores.join(" ")));
      return;
    }
    const primera = todas[0];
    const faltaDestino =
      modo === "entregar" ? errorDestino(primera, vendedor) : null;
    if (faltaDestino) {
      setError(new Error(faltaDestino));
      return;
    }
    if (
      modo === "entregar" &&
      !(await confirmar(
        primera.canal === "pdf"
          ? {
              titulo: `Guardar ${todas.length} propuestas y generar el PDF comparativo`,
              confirmar: "Guardar y generar",
            }
          : {
              titulo: `Enviar ${todas.length} propuestas por ${NOMBRE_CANAL[primera.canal]}`,
              mensaje:
                "Si un intento anterior falló, comprueba primero el destino para evitar duplicados.",
              confirmar: "Enviar",
            },
      ))
    )
      return;
    lock.current = true;
    setBusy(true);
    setError(null);
    setPdf("");
    let confirmadas = 0;
    try {
      const resultados: Resultado[] = [];
      // Validar todas antes de empezar a guardar o entregar.
      for (const c of todas) {
        setStatus(`Validando ${c.etiquetaPropuesta}…`);
        const anterior =
          modo !== "comparativo" ? recibos.current.get(huella(c)) : undefined;
        const key = useEditor.getState().claveGuardado(huella(c), c.numero);
        const r =
          anterior?.r ??
          (await api.calcular(construirPayload(c, vendedor, key)));
        if (
          Math.abs(r.total_general_interno - calcularCotizacion(c).total) >
            0.01 &&
          !(await confirmar({
            titulo: `${c.etiquetaPropuesta}: el precio validado no coincide`,
            mensaje: `El precio validado es ${moneda(r.total_general_interno)}, distinto de la vista previa.`,
            confirmar: "Continuar con este precio",
          }))
        ) {
          setStatus("Operación cancelada antes del guardado.");
          return;
        }
        resultados.push({ c, r });
      }
      setTotales(resultados);
      if (modo === "comparativo") {
        await comparativo(resultados);
        setStatus(
          "PDF comparativo listo. Este botón no guarda ni envía las propuestas.",
        );
        return;
      }
      const entregar = modo === "entregar";
      // La primera propuesta guardada fija la base oficial (…-0001); las
      // siguientes la piden para quedar como …-0001-B, -C.
      let base = primera.numeroBaseOficial ?? null;
      for (const resultado of resultados) {
        const fingerprint = huella(resultado.c),
          anterior = recibos.current.get(fingerprint);
        if (anterior && (!entregar || anterior.entregada)) {
          resultado.c = anterior.c;
          base ??= anterior.c.numeroBaseOficial ?? null;
        } else {
          const c = { ...resultado.c, numeroBaseOficial: base },
            r = resultado.r;
          setStatus(`Guardando ${c.etiquetaPropuesta}…`);
          const key = useEditor
            .getState()
            .claveGuardado(
              fingerprint + (entregar ? "|entregar" : ""),
              c.numero,
            );
          const saved = await api.guardar(
            construirPayload(c, vendedor, key, entregar),
            vendedor,
            c.cliente.nombre,
            r.total_general_interno,
          );
          useEditor.getState().guardarRegistro(c.numero, saved);
          base = saved.numeroBase;
          resultado.c = {
            ...c,
            registroId: saved.id,
            revision: saved.revision,
            numeroOficial: saved.numero,
            numeroBaseOficial: saved.numeroBase,
          };
          recibos.current.set(fingerprint, {
            ...resultado,
            enviado: false,
            entregada: entregar,
          });
        }
        confirmadas++;
      }
      useEditor.getState().marcarGuardada();
      if (modo === "entregar") {
        if (primera.canal === "pdf") await comparativo(resultados);
        else
          for (const { c, r } of resultados) {
            const recibo = recibos.current.get(huella(c))!;
            if (recibo.enviado) continue;
            const incluirSitiosEnCero = (recibo.incluirSitiosEnCero ??=
              await preguntarSitiosEnCero(c, r));
            const enviadoPor = (recibo.enviadoPor ??= []);
            for (const canal of CANALES_ENVIO[c.canal]) {
              if (enviadoPor.includes(canal)) continue;
              setStatus(
                `Enviando ${c.etiquetaPropuesta} por ${NOMBRE_CANAL[canal]}…`,
              );
              const res = await api.post<{
                ok?: boolean;
                error?: string;
                via?: string;
              }>(
                "cotizador-generar-pdf",
                construirPdf({ ...c, canal }, r, vendedor, {
                  incluirSitiosEnCero,
                }),
                120000,
              );
              if (res?.error || res?.ok === false)
                throw new Error(
                  `${c.etiquetaPropuesta} (${NOMBRE_CANAL[canal]}): ${res.error || "el envío no fue confirmado."}`,
                );
              enviadoPor.push(canal);
              if (canal === "whatsapp")
                recibo.porCrm = res?.via === "plantilla";
            }
            recibo.enviado = true;
          }
      }
      setStatus(
        `${confirmadas} propuestas guardadas (${base}).${modo === "entregar" ? (primera.canal === "pdf" ? " PDF comparativo listo." : textoEnvios(todas) || " Envíos confirmados.") : ""}`,
      );
    } catch (e) {
      setError(e);
      setStatus(
        `${confirmadas} de ${todas.length} guardados confirmados en esta operación.${textoEnvios(todas)} Conservamos tus datos; puedes reintentar lo pendiente. Si falló un envío, verifica el destino antes de repetirlo.`,
      );
    } finally {
      void client.invalidateQueries({ queryKey: ["historial"] });
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="actions card">
      <ErrorAviso error={error} />
      <EstadoOperacion texto={status} fallo={!!error} />
      {totales.length ? (
        <dl aria-label="Totales por propuesta">
          {totales.map(({ c, r }) => (
            <div key={c.numero}>
              <dt>{c.etiquetaPropuesta}</dt>
              <dd>{moneda(r.total_general_interno)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <div className="row acciones-botones">
        <button onClick={() => void ejecutar("guardar")}>
          <Icono nombre="guardar" /> Guardar propuestas
        </button>
        <button onClick={() => void ejecutar("comparativo")}>
          <Icono nombre="archivo" /> Generar PDF comparativo
        </button>
        <button className="primary" onClick={() => void ejecutar("entregar")}>
          <Icono nombre="enviar" /> Confirmar y entregar propuestas
        </button>
        {pdf ? (
          <a className="button" href={pdf} download={nombrePdf}>
            <Icono nombre="descargar" /> Descargar PDF comparativo
          </a>
        ) : null}
      </div>
    </section>
  );
}
